/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/** @module VsLogsModule */

import {
    AfterViewInit,
    Component,
    OnInit,
    TemplateRef,
    ViewChild,
} from '@angular/core';

import { FilterOperatorType, IVsLogsSignificanceSignpostData } from 'ng/modules/vs-logs/vs-logs.types';

import { customTemplates, templatesConfiguration } from 'ng/modules/vs-logs/constants/vs-logs-signpost.constants';

import { L10nService } from '@vmw/ngx-vip';
import { IAviDataGridConfig } from 'ng/modules/data-grid';
import { getSignificanceRequestParams } from 'ng/modules/vs-logs/utils/vs-logs-signpost.utils';
import { VsLogsSignpostStore } from '../../vs-logs-signpost.store';
import * as l10n from './vs-logs-signpost-custom-significance.l10n';
import './vs-logs-signpost-custom-significance.component.less';

const { ENGLISH: dictionary, ...l10nKeys } = l10n;

/**
 * @description Signpost component for significance section.
 * @author Suraj Kumar
 */
@Component({
    selector: 'vs-logs-signpost-custom-significance',
    templateUrl: './vs-logs-signpost-custom-significance.component.html',
})
export class VsLogsSignpostCustomSignificanceComponent implements OnInit, AfterViewInit {
    /**
     * Datagrid template for name column.
     */
    @ViewChild('nameFieldTemplateRef')
    public readonly nameFieldTemplateRef: TemplateRef<HTMLElement>;

    /**
     * Datagrid template for column progress bar.
     */
    @ViewChild('progressBarFieldTemplateRef')
    public readonly progressBarFieldTemplateRef: TemplateRef<HTMLElement>;

    public significanceGroupsGridConfig: IAviDataGridConfig;

    public readonly FilterOperatorType = FilterOperatorType;

    public readonly l10nKeys = l10nKeys;

    constructor(
        public readonly vsLogsSignpostStore: VsLogsSignpostStore,
        private readonly l10nService: L10nService,
    ) {
        this.l10nService.registerSourceBundles(dictionary);
    }

    /** @override */
    public ngOnInit(): void {
        const fullSignpostParams = getSignificanceRequestParams();

        this.vsLogsSignpostStore.getVsLogSignpostData(fullSignpostParams);
    }

    /** @override */
    public ngAfterViewInit(): void {
        const { l10nService } = this;
        const configKey = customTemplates.significance;

        this.significanceGroupsGridConfig = {
            layout: {
                hidePagination: true,
                hideCheckboxes: true,
                showFooter: false,
            },
            fields: [
                {
                    label: templatesConfiguration[configKey].name,
                    id: 'name',
                    templateRef: this.nameFieldTemplateRef,
                    expandedTemplateRef: this.nameFieldTemplateRef,
                },
                {
                    label: l10nService.getMessage(
                        l10nKeys.logsNumberLabel,
                    ),
                    id: 'logs',
                    transform: (
                        row: IVsLogsSignificanceSignpostData,
                    ): number => row.count,
                    expandedTransform: (
                        row: IVsLogsSignificanceSignpostData,
                    ): number => row.count,
                },
                {
                    label: l10nService.getMessage(l10nKeys.logsPercentageLabel),
                    id: 'percentage',
                    transform: (
                        row: IVsLogsSignificanceSignpostData,
                    ): string => `${row.percentage}%`,
                    expandedTransform: (
                        row: IVsLogsSignificanceSignpostData,
                    ): string => `${row.percentage}%`,
                },
                {
                    label: '',
                    id: 'progress-bar',
                    templateRef: this.progressBarFieldTemplateRef,
                    expandedTemplateRef: this.progressBarFieldTemplateRef,
                },
            ],
            isExpandedContentColumnBased: true,
        };
    }
}
